// Client for the server-authoritative game-progress API (/api/progress).
//
// The server owns started_at and the computed solve time. The client:
//   - calls begin() when the guest passes the welcome modal (stamps the clock),
//   - calls save() after each guess (persist, cross-device/clear-proof),
//   - calls restore() on load to rehydrate the board from the server.
//
// All calls carry the guest token (Authorization: Bearer). Without a token the
// server returns 401; callers fall back to local-only play.
import { authHeaders, getToken } from './identity'

const PROGRESS_API = process.env.REACT_APP_PROGRESS_API || '/api/progress'

export type ServerProgress = {
  puzzleDay: number
  startedAt: number
  guesses: string[]
  finished: boolean
  won: boolean | null
  guessCount: number | null
  timeMs: number | null
}

const jsonHeaders = (): Record<string, string> => ({
  'content-type': 'application/json',
  ...authHeaders(),
})

// Stamp started_at (idempotent). Called on welcome-modal Start.
export const beginGame = async (
  day: number
): Promise<ServerProgress | null> => {
  if (!getToken()) return null
  try {
    const res = await fetch(PROGRESS_API, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ day }),
    })
    if (!res.ok) return null
    const data = await res.json()
    return (data.progress as ServerProgress) ?? null
  } catch {
    return null
  }
}

// Persist the current guess list; set finished=true (with won) at game end.
export const saveProgress = async (params: {
  day: number
  guesses: string[]
  finished: boolean
  won: boolean
}): Promise<ServerProgress | null> => {
  if (!getToken()) return null
  try {
    const res = await fetch(PROGRESS_API, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(params),
    })
    if (!res.ok) return null
    const data = await res.json()
    return (data.progress as ServerProgress) ?? null
  } catch {
    return null
  }
}

// Restore this guest's board from the server (null if no token / nothing saved).
export const restoreProgress = async (): Promise<ServerProgress | null> => {
  if (!getToken()) return null
  try {
    const res = await fetch(PROGRESS_API, {
      method: 'GET',
      headers: { accept: 'application/json', ...authHeaders() },
    })
    if (!res.ok) return null
    const data = await res.json()
    return (data.progress as ServerProgress) ?? null
  } catch {
    return null
  }
}
